import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useStyletron } from 'parkdepot-shared/theme'
import { useTranslation } from 'react-i18next'

// components
import LoadingAnimation from '../../../../components/LoadingAnimation/LoadingAnimation.component'
import LotCard from '../../../../components/LotCard/LotCard.component'

// style components
import Text from '@components/style/Text.style'
import {
    StartWrapper,
    Container,
    NoAvailableLotsContainer,
    NoLotsIcon,
    NoLotsText,
    Subtitle,
    HiddenContainer
} from './LotListView.style'
import { useFetchLots } from '../../../../hooks/useFetchLots.hook'
import { Lot } from '../../../../types/lot.interface'
import { MapLocation } from '../../../../types/mapLocation.interface'
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll.component'
import { lotRoute } from '@views/Lot/route'

const LOTS_PER_SCROLL = 8

interface Props {
    searchable?: boolean
    searchedLocation?: MapLocation | null
    showTitleAndCount?: boolean
    onSearchReset?: () => void
}

const LotListView: FC<Props> = ({ searchable, searchedLocation = null, showTitleAndCount = true, onSearchReset }) => {
    const { t } = useTranslation()
    const [, { typography }] = useStyletron()

    const { loading, displayedLotIds, fetchLotData } = useFetchLots(searchedLocation)

    const listInnerRef = useRef<HTMLDivElement>(null)
    const [displayedCards, setDisplayedCards] = useState<Lot[]>([])
    const [fetchingLotData, setFetchingLotData] = useState<boolean>(false)

    const fetchLotDataWhenVisible = async (lotIds: string[]) => {
        if (lotIds.length === 0) {
            return
        }
        setFetchingLotData(true)
        try {
            const lots = await Promise.all(lotIds.map((lotId) => fetchLotData(lotId)))
            if (lots && lots.length > 0) {
                const newLots = lots.filter((lot: any) => !displayedCards.find((prevLot) => prevLot.id === lot?.id))
                setDisplayedCards([...displayedCards, ...(newLots as Lot[])])
            }
        } catch (error) {
            console.error('Error fetching lot data', error)
        }
        setFetchingLotData(false)
    }

    const handleScroll = useCallback(() => {
        if (fetchingLotData) return
        fetchLotDataWhenVisible(displayedLotIds.slice(displayedCards.length, displayedCards.length + LOTS_PER_SCROLL))
    }, [displayedLotIds, displayedCards, fetchingLotData])

    const renderHiddenSeoLinks = useMemo(() => {
        return displayedLotIds
            .filter((lotId) => !displayedCards.find((lot) => lot?.id === lotId))
            .map((lotId) => (
                <a key={lotId} href={lotRoute(lotId)}>
                    {lotId}
                </a>
            ))
    }, [displayedLotIds, displayedCards])

    useEffect(() => {
        if (searchedLocation) {
            setDisplayedCards([])
        }
    }, [searchedLocation])

    useEffect(() => {
        // fetch initial lots
        fetchLotDataWhenVisible(displayedLotIds.slice(0, LOTS_PER_SCROLL))
    }, [displayedLotIds[0]])

    return (
        <Container ref={listInnerRef} $displayedLots={displayedCards.length > 3}>
            {showTitleAndCount && (
                <StartWrapper>
                    <Subtitle>{t('search.sectionTitle')}</Subtitle>
                    <Text typography={typography.ParagraphSmall} fontWeight={300}>
                        {t('search.lotsFound', { count: loading ? 0 : displayedLotIds.length })}
                    </Text>
                </StartWrapper>
            )}
            {!loading &&
                displayedCards.map((lot) => (lot ? <LotCard key={lot.id} lot={lot} smallImage={searchable} /> : null))}
            {(loading || fetchingLotData) && <LoadingAnimation />}
            <InfiniteScroll onIntersect={handleScroll} />
            {!loading && <HiddenContainer aria-hidden="true">{renderHiddenSeoLinks}</HiddenContainer>}
            {!loading && displayedLotIds.length === 0 && (
                <NoAvailableLotsContainer
                    onClick={() => {
                        if (onSearchReset) {
                            onSearchReset()
                        }
                    }}
                >
                    <NoLotsIcon className="wemolo-map-search" aria-hidden="true" />
                    <NoLotsText>{t('search.showAllAvailableLots')}</NoLotsText>
                </NoAvailableLotsContainer>
            )}
        </Container>
    )
}

export default LotListView
